import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type FiltersState = {
  skillIds: number[];
  jurisdictionIds: number[];
  allCountry: boolean;
  page: number;
  search: string;
};

type UseProfesionalesFiltersArgs = {
  onApply: (filters: FiltersState) => void;
  initialPage?: number;
};

const useProfesionalesFilters = ({ onApply, initialPage = 1 }: UseProfesionalesFiltersArgs) => {
  const [skillIds, setSkillIds] = useState<number[]>([]);
  const [jurisdictionIds, setJurisdictionIds] = useState<number[]>([]);
  const [allCountry, setAllCountry] = useState(false);
  const [page, setPage] = useState(initialPage);
  const [search, setSearchState] = useState("");
  const shouldSyncRef = useRef(false);

  const toggleSkill = useCallback(
    (id: number) => {
      shouldSyncRef.current = true;
      setSkillIds((prev) => {
        const nextSkillIds = prev.includes(id) ? [] : [id];
        return nextSkillIds;
      });
      setPage(1);
    },
    []
  );

  const toggleJurisdiction = useCallback(
    (id: number) => {
      shouldSyncRef.current = true;
      setJurisdictionIds((prev) => (prev.includes(id) ? [] : [id]));
      setAllCountry(false);
      setPage(1);
    },
    []
  );

  const toggleAllCountry = useCallback(() => {
    shouldSyncRef.current = true;
    setAllCountry((prev) => {
      const nextAllCountry = !prev;
      const nextJurisdictions = nextAllCountry ? [] : jurisdictionIds;
      return nextAllCountry;
    });
    setJurisdictionIds([]);
    setPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    shouldSyncRef.current = true;
    setSkillIds([]);
    setJurisdictionIds([]);
    setAllCountry(false);
    setSearchState("");
    setPage(1);
  }, []);

  const setSearch = useCallback((term: string) => {
    shouldSyncRef.current = true;
    setSearchState(term);
    setPage(1);
  }, []);

  const applyFilters = useCallback(() => {
    const filters: FiltersState = {
      skillIds,
      jurisdictionIds,
      allCountry,
      page,
      search,
    };
    onApply(filters);
  }, [allCountry, jurisdictionIds, onApply, page, skillIds, search]);

  const applyQuickSkill = useCallback(
    (skillId: number) => {
      const nextSkillIds = skillIds.includes(skillId) ? [] : [skillId];
      shouldSyncRef.current = true;
      setSkillIds(nextSkillIds);
      setPage(1);
    },
    [allCountry, jurisdictionIds, skillIds]
  );

  const setPageAndApply = useCallback(
    (nextPage: number) => {
      shouldSyncRef.current = true;
      setPage(nextPage);
    },
    [allCountry, jurisdictionIds, skillIds]
  );

  const filtersCount = useMemo(() => {
    let count = 0;
    if (skillIds.length > 0) count += 1;
    if (jurisdictionIds.length > 0) count += 1;
    if (allCountry) count += 1;
    if (search.trim().length > 0) count += 1;
    return count;
  }, [allCountry, jurisdictionIds.length, skillIds.length, search]);

  const initialize = useCallback(
    (initial: FiltersState) => {
      shouldSyncRef.current = true;
      setSkillIds(initial.skillIds);
      setJurisdictionIds(initial.jurisdictionIds);
      setAllCountry(initial.allCountry);
      setSearchState(initial.search ?? "");
      setPage(initial.page);
    },
    []
  );

  useEffect(() => {
    if (!shouldSyncRef.current) {
      return;
    }
    const filters: FiltersState = {
      skillIds,
      jurisdictionIds,
      allCountry,
      page,
      search,
    };
    onApply(filters);
  }, [skillIds, jurisdictionIds, allCountry, page, search, onApply]);

  return {
    state: {
      skillIds,
      jurisdictionIds,
      allCountry,
      page,
      search,
    },
    actions: {
      toggleSkill,
      toggleJurisdiction,
      toggleAllCountry,
      clearFilters,
      applyFilters,
      applyQuickSkill,
      setPage: setPageAndApply,
      setSearch,
      initialize,
    },
    meta: {
      filtersCount,
    },
  };
};

export default useProfesionalesFilters;

